import type { MarketViewScores, ScoreDimension } from '@/services/api'

/**
 * One place that decides what a score dimension actually means (DEV-584).
 *
 * The backend has always returned an int for `value`, including for dimensions
 * it never measured - standalone `/market` hardcodes 50 for the three vendor
 * scorecards, and several failure exits return 50 as well. Rendering that
 * number paints an unmeasured axis as a mid-range assessment.
 *
 * `describeScore` is the only thing that should read a raw `.value`. It hands
 * callers a number when there is one and `null` when there is not, so no render
 * site can plot, colour or average a placeholder by accident.
 */

export type ScoreState = 'scored' | 'not_applicable' | 'not_scored' | 'error'

interface ScoreDisplayBase {
  /** What belongs in the value slot: '81', 'N/A', 'Not scored', 'Scoring failed'. */
  text: string
  /** Grade word when scored, state word otherwise. */
  label: string
  explanation: string
}

/**
 * Discriminated on `isScored` so a `score.isScored` guard narrows `value` to a
 * number. Without that, every call site needs a non-null assertion to colour or
 * position a bar, and an assertion is exactly the habit this module removes.
 */
export type ScoreDisplay = ScoreDisplayBase &
  (
    | { state: 'scored'; isScored: true; value: number }
    | { state: Exclude<ScoreState, 'scored'>; isScored: false; value: null }
  )

/** Value-slot copy per non-measurement state. Differentiated by word, not colour. */
const STATE_TEXT: Record<Exclude<ScoreState, 'scored'>, string> = {
  not_applicable: 'N/A',
  not_scored: 'Not scored',
  error: 'Scoring failed',
}

/**
 * Legacy fallback for payloads that predate `status`.
 *
 * Search results are serialized into Redis at `mv:search:{search_id}`, so
 * page-2 and rescore responses can be pre-deploy for the life of a cache entry.
 * The heuristic lives here rather than at the call sites so callers keep a
 * single code path; it can be deleted once no cached payload lacks `status`.
 */
const LEGACY_LABEL_STATE: Record<string, ScoreState> = {
  'N/A': 'not_applicable',
  'No Program': 'not_applicable',
  'No Scorecard': 'not_scored',
  'Empty Scorecard': 'not_scored',
  'No Specs': 'not_scored',
  'No Criteria': 'not_scored',
  'No Data': 'not_scored',
  'Scoring Error': 'error',
  Error: 'error',
}

const KNOWN_STATES: ScoreState[] = ['scored', 'not_applicable', 'not_scored', 'error']

function resolveState(dimension: ScoreDimension): ScoreState {
  const declared = dimension.status as ScoreState | undefined
  if (declared && KNOWN_STATES.includes(declared)) return declared

  // No status, or one this build does not know about: fall back to the label.
  return LEGACY_LABEL_STATE[dimension.label] ?? 'scored'
}

export function describeScore(
  dimension: ScoreDimension | null | undefined
): ScoreDisplay {
  if (!dimension) {
    // An absent dimension is not a zero and not an error - nothing was reported.
    return {
      state: 'not_scored',
      isScored: false,
      value: null,
      text: STATE_TEXT.not_scored,
      label: STATE_TEXT.not_scored,
      explanation: '',
    }
  }

  const state = resolveState(dimension)
  const explanation = dimension.explanation ?? ''

  if (state === 'scored' && Number.isFinite(dimension.value)) {
    return {
      state: 'scored',
      isScored: true,
      value: dimension.value,
      text: String(Math.round(dimension.value)),
      label: dimension.label,
      explanation,
    }
  }

  // A 'scored' dimension whose value is not a finite number is a bug upstream,
  // but it must not reach the DOM as NaN/undefined or the agent as a score.
  const fallback: Exclude<ScoreState, 'scored'> =
    state === 'scored' ? 'not_scored' : state

  return {
    state: fallback,
    isScored: false,
    value: null,
    text: STATE_TEXT[fallback],
    label: STATE_TEXT[fallback],
    explanation,
  }
}

/**
 * The value to put in an agent context payload.
 *
 * The omnipresent agent reads the viewport JSON and quotes it back to the user,
 * so a bare 50 there is the same defect as a 50 on the chart - it just gets
 * spoken instead of drawn. Measured dimensions stay numeric so the agent can
 * compare them; the rest carry their state word.
 */
export function agentScoreValue(
  dimension: ScoreDimension | null | undefined
): number | string {
  const score = describeScore(dimension)
  return score.isScored ? score.value : score.text
}

/**
 * The same treatment for the overall score.
 *
 * `overall` is a rollup of the five dimensions, so it inherits their honesty
 * problem: standalone `/market` defines it as the technical score alone, and
 * the scoring error path sets it to 0. The backend now sends `overall_status`;
 * when it is missing (a payload cached before this shipped) the state is
 * derived from the dimensions, which is the same rule the backend applies.
 */
export function describeOverall(scores: MarketViewScores): ScoreDisplay {
  const dimensions = [
    scores.need_fit,
    scores.technical,
    scores.manufacturing,
    scores.vc_metrics,
    scores.dod_partnership,
  ]

  const declared = scores.overall_status as ScoreState | undefined
  const state: ScoreState =
    declared && KNOWN_STATES.includes(declared) ? declared : rollupState(dimensions)

  return describeScore({
    value: scores.overall,
    label: '',
    explanation: '',
    status: state,
  })
}

/** Mirrors `_rollup_status` in `unified_scoring_service.py`. Keep them in step. */
function rollupState(dimensions: (ScoreDimension | null | undefined)[]): ScoreState {
  const states = new Set(dimensions.map((d) => describeScore(d).state))

  if (states.has('scored')) return 'scored'
  if (states.size === 1) return [...states][0]
  return 'not_scored'
}

/**
 * Colour classes for an overall-score badge, so the three cards that render one
 * stay in step.
 *
 * A non-measurement is muted rather than banded. The error path sets `overall`
 * to 0, which the 0-39 band would have painted destructive red - an alarming
 * colour for a number nobody computed. Callers pick the type scale, since the
 * state words want a smaller size than a two-digit score.
 */
export function overallBadgeClasses(score: ScoreDisplay): string {
  if (!score.isScored) return 'bg-muted text-muted-foreground'
  if (score.value >= 80) return 'bg-success/10 text-success'
  if (score.value >= 60) return 'bg-primary/10 text-primary'
  if (score.value >= 40) return 'bg-warning/10 text-warning'
  return 'bg-destructive/10 text-destructive'
}

/**
 * The overall score as an agent context payload value. See `agentScoreValue`.
 */
export function agentOverallValue(
  scores: MarketViewScores | null | undefined
): number | string {
  if (!scores) return STATE_TEXT.not_scored
  const score = describeOverall(scores)
  return score.isScored ? score.value : score.text
}
